import { useState } from "react";
import { AlertCircle, ArrowRight, Loader2, Lock, Mail, User } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import LegalFooter from "../components/LegalFooter";
import WelcomeModal from "../components/WelcomeModal";
import { api } from "../lib/api";

function formatDetail(detail) {
  if (!detail) return "Algo salió mal. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [welcomeName, setWelcomeName] = useState(null);

  function signInWithGoogle() {
    window.localStorage.removeItem("onboarding_draft");
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const url = mode === "register" ? "/auth/register" : "/auth/login";
      const body = mode === "register" ? { name, email, password } : { email, password };
      const r = await api.post(url, body, { withCredentials: true });
      if (r.data?.session_token) {
        window.localStorage.setItem("session_token", r.data.session_token);
      }
      if (mode === "register") {
        // Show welcome + start trial before entering the app
        setWelcomeName(r.data?.user?.name || name);
      } else {
        navigate("/");
        window.location.reload();
      }
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  }

  const canSubmit = email && password && (mode === "login" || (name && name.length >= 2 && acceptTerms && acceptPrivacy));

  return (
    <main className="onboarding-page login-page-v2">
      <nav className="landing-topnav" data-testid="landing-topnav">
        <Link to="/" style={{ textDecoration: "none" }}>
          <Logo variant="mark" size="sm" />
        </Link>
        <div className="landing-topnav-links">
          <button type="button" onClick={() => setMode("login")} data-testid="landing-login-btn">Iniciar Sesión</button>
          <button type="button" className="primary" onClick={() => setMode("register")} data-testid="landing-register-btn">Crear Cuenta</button>
          <Link to="/legal/privacidad" data-testid="landing-privacy-btn">Aviso de Privacidad</Link>
        </div>
      </nav>
      {welcomeName !== null && (
        <WelcomeModal userName={welcomeName} onClose={() => { setWelcomeName(null); window.location.assign("/"); }} />
      )}
      <div className="landing-hero-grid" data-testid="landing-hero-grid">
      <section className="onboarding-card login-card-v2" data-testid={mode === "register" ? "register-page" : "login-page"}>
        <div className="login-brand" data-testid="login-brand">
          <Logo variant="full" size="lg" animate testid="login-brand-logo" />
        </div>
        <h1 data-testid="login-title">{mode === "register" ? <>Crea tu cuenta, <em>¡es gratis!</em></> : <>Bienvenido, <em>¡nos alegra verte!</em></>}</h1>
        <p>{mode === "register" ? "Empieza a organizar tus finanzas en minutos." : "Inicia sesión para continuar donde te quedaste."}</p>

        <button type="button" className="google-btn" onClick={signInWithGoogle} disabled={busy} data-testid="google-login-button">
          <svg width="18" height="18" viewBox="0 0 24 24" style={{ marginRight: 8 }}><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Continuar con Google
        </button>

        <div className="login-divider" data-testid="login-divider"><span>o</span></div>

        <form onSubmit={submit} className="login-form" data-testid="email-form">
          {mode === "register" && (
            <label className="login-field">
              <span>Nombre completo</span>
              <div className="login-input-wrap">
                <User size={16} />
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" autoComplete="name" required data-testid="register-name" />
              </div>
            </label>
          )}
          <label className="login-field">
            <span>Email</span>
            <div className="login-input-wrap">
              <Mail size={16} />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.com" autoComplete="email" required data-testid="login-email" />
            </div>
          </label>
          <label className="login-field">
            <span>Contraseña</span>
            <div className="login-input-wrap">
              <Lock size={16} />
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres" autoComplete={mode === "register" ? "new-password" : "current-password"} minLength={6} required data-testid="login-password" />
            </div>
          </label>

          {error && (
            <div className="login-error" data-testid="login-error"><AlertCircle size={15} />{error}</div>
          )}

          {mode === "register" && (
            <div className="login-legal" data-testid="register-legal">
              <label className="login-checkbox">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  data-testid="accept-terms-checkbox"
                />
                <span>He leído y acepto los <Link to="/legal/terminos" target="_blank">Términos y Condiciones</Link>.</span>
              </label>
              <label className="login-checkbox">
                <input
                  type="checkbox"
                  checked={acceptPrivacy}
                  onChange={(e) => setAcceptPrivacy(e.target.checked)}
                  data-testid="accept-privacy-checkbox"
                />
                <span>Confirmo que he leído el <Link to="/legal/privacidad" target="_blank">Aviso de Privacidad</Link>.</span>
              </label>
            </div>
          )}

          <button type="submit" className="onboarding-primary" disabled={!canSubmit || busy} data-testid={mode === "register" ? "register-submit" : "login-submit"}>
            {busy ? <Loader2 size={18} className="spin" /> : (mode === "register" ? "Crear cuenta" : "Iniciar sesión")} {!busy && <ArrowRight size={16} />}
          </button>
        </form>

        <div className="login-switch">
          {mode === "login" ? (
            <>¿Aún no tienes cuenta? <button type="button" onClick={() => { setMode("register"); setError(""); }} data-testid="switch-to-register">Crear cuenta</button></>
          ) : (
            <>¿Ya tienes cuenta? <button type="button" onClick={() => { setMode("login"); setError(""); }} data-testid="switch-to-login">Iniciar sesión</button></>
          )}
        </div>

        <button type="button" className="login-forgot" disabled data-testid="forgot-password" title="Próximamente">¿Olvidaste tu contraseña?</button>
      </section>
      <aside className="landing-hero-art" data-testid="landing-hero-art" aria-hidden="true">
        <img
          src="/hero/mi-contador-art.jpg"
          alt=""
          loading="eager"
          decoding="async"
          draggable="false"
        />
      </aside>
      </div>
      <LegalFooter variant="onboarding" />
    </main>
  );
}
